import React, { useEffect } from "react";
import { Button, Modal, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { tableHeader } from "~/pages/Editor/EditorReports/ui/CatalogsOfReports/config/options";
import { tableDataFormatter } from "~/pages/Editor/EditorReports/ui/CatalogsOfReports/config/tableDataFormatter";
import {
  IndicatorGroup,
  useGetIndicatorGroupsLazyQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2 } from "~/shared/ui/TableV2";

import ReportManagement from "./ReportManagement";
import { getCatalogsOfReportsColumns } from "./model/tableAdapter";

import s from "./Reports.less";

interface ReportsProps {
  onHandleClickByRows: (id: IndicatorGroup) => void;
}
const CatalogsOfReports = ({ onHandleClickByRows }: ReportsProps) => {
  const [getReports, reportsState] = useGetIndicatorGroupsLazyQuery({
    fetchPolicy: "no-cache",
  });
  const { t } = useTranslation();
  const history = useHistory();
  const closeModal = () => {
    history.replace(window.location.pathname);
  };
  const { hasPermission } = usePermissions();
  const { sortField, sortType, openModal, currentId, page, count } =
    useGetLocationData();
  const setDataReplace = useSetLocationData(true);

  const isOpenAddModal = openModal === "Add";
  const isOpenEditModal = openModal === "Edit";

  const data: IndicatorGroup[] = get(
    reportsState.data,
    "IndicatorGroupReportGetter.getIndicatorGroups.items",
    [],
  );
  const totalItems = get(
    reportsState.data,
    "IndicatorGroupReportGetter.getIndicatorGroups.total",
    0,
  );

  const formattedData: IndicatorGroup[] = tableDataFormatter(data, t);
  const currentItem = data.find((el) => el.id === currentId);

  const onHandleClick = (item: IndicatorGroup): void => {
    onHandleClickByRows(item);
  };

  const { handleCount, handlePage } = usePaginationParams();

  const handleRowEdit = (record: { id?: string | number }) => {
    setDataReplace("openModal", "Edit", "currentId", record.id);
  };

  const getReportsGroup = () =>
    getReports({ variables: { sortType, sortField, page, count } });

  useEffect(() => {
    if (isOpenAddModal || isOpenEditModal) {
      closeModal();
    }
    getReportsGroup();
  }, [sortType, sortField, page, count]);

  const goBack = () => {
    history.replace("/editor/");
  };

  const openAddModal = () => setDataReplace("openModal", "Add");

  const formId = "ReportManagement";

  return (
    <Wrapper.Content
      title={t("reports.mainTitle")}
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("indicatorGroupReports.indicatorGroup.create") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("reports_list.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={getCatalogsOfReportsColumns(t, tableHeader)}
        dataSource={formattedData}
        loading={reportsState.loading}
        onRowEdit={
          hasPermission("indicatorGroupReports.indicatorGroup.edit")
            ? handleRowEdit
            : undefined
        }
        onRow={(record) => ({
          onClick: () => onHandleClick(record),
          style: { cursor: "pointer" },
        })}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={totalItems}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />

      <Modal
        title={
          isOpenAddModal
            ? t("reports_list.createIndicatorGroup")
            : t("reports_list.editIndicatorGroup")
        }
        open={isOpenAddModal || isOpenEditModal}
        onCancel={closeModal}
        closable
        centered
        okButton={{
          htmlType: "submit",
          form: formId,
          children: t("ui.save"),
        }}
        destroyOnHidden
      >
        <ReportManagement
          currentItem={currentItem}
          refetch={getReportsGroup}
          closeModal={closeModal}
          formId={formId}
        />
      </Modal>
    </Wrapper.Content>
  );
};

export default CatalogsOfReports;
